/**
 * Seeded, reproducible pseudo-randomness.
 *
 * Shared rather than duplicated: the read planner needs it to place sample
 * offsets deterministically from `(sha, tensor)`, and the tests need it to build
 * reproducible inputs. Two copies drifting apart would be a subtle way to break
 * the promise that a deep link always shows the same picture.
 *
 * Not cryptographic and not intended to be. The only requirements are that the
 * same seed always yields the same stream, and that streams from different
 * seeds don't share a phase.
 */

/**
 * FNV-1a over a string, giving a 32-bit seed.
 *
 * `Math.imul` keeps the multiply in 32-bit territory; a plain `*` would lose the
 * low bits to float rounding once the product exceeds 2^53.
 */
export function seedFrom(...parts: readonly string[]): number {
  let hash = 0x811c9dc5;
  const input = parts.join("\u0000");
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 — small, fast, and identical across JS engines. */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
